import React from 'react';
import InventoryDesktopHead from './InventoryDesktopHead';
import { Filter } from '../../../@types/inventory';
import { IInventory, IPaginationMeta} from '../../../@types';
import ListInventoryBody from './ListInventoryBody';
import { InventoryMobileHead } from './InventoryMobileHead';
import { FloatingScrollButton } from '../../FloatingScrollButton';
import Empty from '../../common/Table/Empty';
import BinIcon from '../../../assets/icons/BinIcon';
import { Link } from 'react-router-dom';

export type HeadingType = {
  /**
   * Title show on the column head.
   */
  title?: string;
  /**
   * Icon show on the column head.
   */
  icon?: any;
  /**
   * Callback function after the title/icon is clicked.
   * @returns void
   */
  onClick?: () => void;
};

type PaginationProps = {
  /**
   * Show the table footer pagination.
   * The meta prop and fetchMore Data prop will be required
   */
  showPagination?: true;
  /**
   * Pagination meta data
   */
  meta?: IPaginationMeta;
  /**
   * Fetch more data for pagination
   * @param offset The point to start fetching data.
   * @param limit Max number of data to fetch.
   * @param cursor Last data id.
   * @returns Void
   */
  fetchMoreData?: (direction: 'up' | 'down') => void;
};

type HeadProps =
  | {
      showHead?: false;
      buttons?: never;
      handleSearch?: never;
    }
  | {
      /**
       * Show the table head components.
       * @type boolean
       */
      showHead?: true;

      /**
       * List of grouped buttons to show in the head component
       */
      buttons: Array<{
        text: string;
        icon: any;
        href?: string;
        onClick?: () => void;
      }>;
      /**
       * Function called when search text changes.
       * @param text search text
       * @returns void
       */
      handleSearch?: (text: string) => void;
      handleSort?: (text: string) => void;
      selectedFilter?: any;
      handleFilter?: (filter: Filter) => void;
      searchResult: any[];
      searchDisplayKey: string;
      searchPlaceHolder: string;
      handleSearchSelect?: (item: any) => void;
      showMobileInvoiceTable?: boolean;
    };
export type TableItem = {
  [key: string]:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
};

export type TableData = {
  data: [];
};
type ItemProps<T> = {
  /**
   * A HeadingType Object displayed in the table header.
   * The key of this object will be used as key for the items displayed on the table data.
   */
  head: T;
  /**
   * Array of object with keys of HeadingType as it's key.
   * Determines the data that will be rendered in the body of the table
   */
  data: Array<{ [key in keyof T]: any }>;
} & PaginationProps &
  HeadProps;

export type TableProps<T> = {
  /**
   * Indicates whether table data is loading or not.
   */
  isLoading: boolean;
  hideFloatingScrollButton?: boolean;
  showMobileInvoiceTable?: boolean | undefined | any;
  CustomerCard?: boolean | any;
  expenseCard?: boolean | undefined;
  invoiceCard?: boolean | undefined;
  inventoryCard?: boolean | undefined;
  payHistoryTable?: boolean;
} & ItemProps<T>;



const ListTable = (props: any) => {
  const [data, setData] = React.useState<typeof props.data>([]);
  const [sort, setSort] = React.useState<{
    column: string;
    direction: 'ASC' | 'DESC';
  }>({ column: '', direction: 'ASC' });

  const fetchPageData = (direction: 'up' | 'down') => {
    if (direction === 'down' && props?.meta?.hasMoreData) {
      props.fetchMoreData ? props.fetchMoreData('down') : undefined;
    }
  };

  React.useEffect(() => {
    setData(props.data);
  }, [props.data]);

  const tableHead = [
    { dataIndex: 'name', title: 'Product', className: 'text-center' },
    { dataIndex: 'variations', title: 'Variations' },
    { dataIndex: 'price', title: 'Unit Price' },
    { dataIndex: 'quantityLeft', title: 'Qty Left' },
    { dataIndex: 'action', title: 'Action' },
    { dataIndex: 'bin', title: null, icon: <BinIcon />, className: '' },
  ];
  
  return (
    <div>
      <div className="hidden md:block">
        <InventoryDesktopHead
          handleSearchSelect={props.handleSearchSelect}
          buttons={props.buttons}
          handleSearch={props.handleSearch}
          handleSort={props.handleSort}
          searchResult={props.searchResult}
          searchDisplayKey={props.searchDisplayKey}
          handleFilter={props.handleFilter}
          filter={props.selectedFilter}
          searchPlaceHolder={props.searchPlaceHolder}
        />
      </div>
      <div className="md:hidden">
        <InventoryMobileHead
          handleStockStatusChange={props.handleStockStatusChange}
          handleVariantStatusChange={props.handleVariantStatusChange}
          onSubmit={props.onInventoryFilterSubmit}
          selectedFilter={props.selectedFilter}
          handleSearchSelect={props.handleSearchSelect}
          OnSearch={props.handleSearch}
          handleSort={props.handleSort}
          searchResult={props.searchResult}
          searchDisplayKey={props.searchDisplayKey}
          handleFilter={props.handleFilter}
          searchPlaceHolder={props.searchPlaceHolder}
        />
      </div>

      <ListInventoryBody
        inventory={props.inventory}
        data={data}
        isLoading={props.isLoading}
        showPagination={false}
        tableHead={tableHead}

      />

      {data.length === 0 && !props.isLoading && <Empty />}
      {data.length !== 0 && !props.isLoading && (
        <>
          {!props.hideFloatingScrollButton && (
            <FloatingScrollButton
              className="sticky bottom-8 left-[1099px] z-10"
              action={() => {
                if (props.showPagination && props?.meta?.hasMoreData) {
                  fetchPageData('down');
                }
              }}
              title="Scroll Down"
              direction="down"
              fadeMode={!props?.meta?.hasMoreData}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ListTable;
